// frontend/src/pages/EventTemplates.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    EventIcon as Event,
    AddIcon as Add,
    EditIcon as Edit,
    DeleteIcon as Delete,
    CloseIcon as X,
    CalendarIcon as Calendar,
    RepeatIcon as Repeat,
    PlayIcon as Play
} from '../components/Icons';

const EventTemplates = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [templates, setTemplates] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showModal, setShowModal] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        group: '',
        templateData: {
            title: '',
            description: '',
            location: '',
            category: 'Gruppenstunde',
            defaultStartTime: '17:00',
            duration: { hours: 2, minutes: 0 }
        },
        recurrence: {
            enabled: false,
            frequency: 'weekly',
            dayOfWeek: 5
        }
    });

    const [generateData, setGenerateData] = useState({
        date: new Date().toISOString().split('T')[0],
        customTitle: ''
    });

    const weekDays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const categories = ['Gruppenstunde', 'Lager', 'Aktion', 'Sonstiges'];

    useEffect(() => {
        fetchTemplates();
        fetchGroups();
        // eslint-disable-next-line
    }, []);

    const fetchTemplates = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.get(`${API_URL}/event-templates`, config);
            setTemplates(data);
            setLoading(false);
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Laden' });
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/groups`);
            setGroups(data);
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };

            if (selectedTemplate) {
                await axios.put(`${API_URL}/event-templates/${selectedTemplate._id}`, formData, config);
                setMessage({ type: 'success', text: 'Vorlage aktualisiert!' });
            } else {
                await axios.post(`${API_URL}/event-templates`, formData, config);
                setMessage({ type: 'success', text: 'Vorlage erstellt!' });
            }

            setShowModal(false);
            resetForm();
            fetchTemplates();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Speichern' });
        }
    };

    const generateEvent = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.post(`${API_URL}/event-templates/${selectedTemplate._id}/generate`, generateData, config);
            setMessage({ type: 'success', text: 'Event erstellt!' });
            setShowGenerateModal(false);
            setGenerateData({ date: new Date().toISOString().split('T')[0], customTitle: '' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Erstellen' });
        }
    };

    const deleteTemplate = async (id) => {
        if (!window.confirm('Vorlage wirklich löschen?')) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/event-templates/${id}`, config);
            setMessage({ type: 'success', text: 'Vorlage gelöscht!' });
            fetchTemplates();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const openEditModal = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            description: template.description || '',
            group: template.group?._id || '',
            templateData: template.templateData || {
                title: '',
                description: '',
                location: '',
                category: 'Gruppenstunde',
                defaultStartTime: '17:00',
                duration: { hours: 2, minutes: 0 }
            },
            recurrence: template.recurrence || {
                enabled: false,
                frequency: 'weekly',
                dayOfWeek: 5
            }
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setSelectedTemplate(null);
        setFormData({
            name: '',
            description: '',
            group: '',
            templateData: {
                title: '',
                description: '',
                location: '',
                category: 'Gruppenstunde',
                defaultStartTime: '17:00',
                duration: { hours: 2, minutes: 0 }
            },
            recurrence: { enabled: false, frequency: 'weekly', dayOfWeek: 5 }
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center shadow-sm">
                        <Event style={{ fontSize: 28 }} />
                    </div>
                    <div>
                        <h1 className="text-display-small font-bold text-surface-on">Event-Vorlagen</h1>
                        <p className="text-body-medium text-surface-onVariant">{templates.length} Vorlagen</p>
                    </div>
                </div>

                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="md3-btn-filled flex items-center gap-2"
                >
                    <Add style={{ fontSize: 20 }} />
                    Neue Vorlage
                </button>
            </div>

            {/* Alert */}
            {message.text && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-fade-in ${message.type === 'error' ? 'bg-error-container text-error-on-container' : 'bg-primary-container text-primary-onContainer'
                    }`}>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
                        <X style={{ fontSize: 18 }} />
                    </button>
                </div>
            )}

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map(template => (
                    <div key={template._id} className="md3-card-elevated p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="text-title-large font-bold text-surface-on">{template.name}</h3>
                                {template.group && (
                                    <span className="text-label-small text-surface-onVariant">{template.group.name}</span>
                                )}
                            </div>
                            {template.recurrence?.enabled && (
                                <span className="px-2 py-1 rounded-full text-label-small bg-tertiary-container text-tertiary-onContainer flex items-center gap-1">
                                    <Repeat style={{ fontSize: 12 }} />
                                    {template.recurrence.frequency}
                                </span>
                            )}
                        </div>

                        {template.description && (
                            <p className="text-body-medium text-surface-onVariant mb-4 line-clamp-2">
                                {template.description}
                            </p>
                        )}

                        <div className="space-y-2 mb-4 text-body-small text-surface-onVariant">
                            <div className="flex items-center gap-2">
                                <Calendar style={{ fontSize: 16 }} />
                                <span>{template.templateData?.category || 'Sonstiges'}</span>
                            </div>
                            {template.recurrence?.enabled && (
                                <div className="flex items-center gap-2">
                                    <Repeat style={{ fontSize: 16 }} />
                                    <span>{weekDays[template.recurrence.dayOfWeek]}</span>
                                </div>
                            )}
                            <div className="text-label-small text-surface-onVariant">
                                {template.usageCount || 0}x verwendet
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-4 border-t border-outlineVariant">
                            <button
                                onClick={() => { setSelectedTemplate(template); setShowGenerateModal(true); }}
                                className="md3-btn-tonal flex-1"
                            >
                                <Play style={{ fontSize: 18 }} className="mr-1" />
                                Event erstellen
                            </button>
                            <button
                                onClick={() => openEditModal(template)}
                                className="md3-icon-btn text-primary hover:bg-primary-container"
                            >
                                <Edit style={{ fontSize: 20 }} />
                            </button>
                            <button
                                onClick={() => deleteTemplate(template._id)}
                                className="md3-icon-btn text-error hover:bg-error-container"
                            >
                                <Delete style={{ fontSize: 20 }} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {templates.length === 0 && (
                <div className="text-center py-16 text-surface-onVariant">
                    <Event style={{ fontSize: 64 }} className="mx-auto mb-4 opacity-30" />
                    <p className="text-title-medium">Keine Vorlagen vorhanden</p>
                    <p className="text-body-medium">Erstellen Sie Ihre erste Event-Vorlage!</p>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-2xl w-full p-8 animate-scale-in max-h-[90vh] overflow-y-auto">
                        <h2 className="text-title-large font-bold mb-6">
                            {selectedTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Vorlagenname *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="md3-textfield-outlined"
                                        placeholder="z.B. Wöchentliche Gruppenstunde"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Gruppe</label>
                                    <select
                                        value={formData.group}
                                        onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                                        className="md3-textfield-outlined"
                                    >
                                        <option value="">-- Keine --</option>
                                        {groups.map(g => (
                                            <option key={g._id} value={g._id}>{g.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Beschreibung</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="md3-textfield-outlined min-h-[80px]"
                                />
                            </div>

                            {/* Template Data */}
                            <div className="p-4 bg-surface-containerLow rounded-xl space-y-4">
                                <h3 className="text-title-small font-bold">Event-Vorlage</h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Event-Titel</label>
                                        <input
                                            type="text"
                                            value={formData.templateData.title}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                templateData: { ...formData.templateData, title: e.target.value }
                                            })}
                                            className="md3-textfield-outlined"
                                            placeholder="z.B. Gruppenstunde Wölflinge"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Kategorie</label>
                                        <select
                                            value={formData.templateData.category}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                templateData: { ...formData.templateData, category: e.target.value }
                                            })}
                                            className="md3-textfield-outlined"
                                        >
                                            {categories.map(c => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Startzeit</label>
                                        <input
                                            type="time"
                                            value={formData.templateData.defaultStartTime}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                templateData: { ...formData.templateData, defaultStartTime: e.target.value }
                                            })}
                                            className="md3-textfield-outlined"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Dauer (h)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={formData.templateData.duration.hours}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                templateData: {
                                                    ...formData.templateData,
                                                    duration: { ...formData.templateData.duration, hours: parseInt(e.target.value) }
                                                }
                                            })}
                                            className="md3-textfield-outlined"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Ort</label>
                                        <input
                                            type="text"
                                            value={formData.templateData.location}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                templateData: { ...formData.templateData, location: e.target.value }
                                            })}
                                            className="md3-textfield-outlined"
                                            placeholder="Pfadfinderheim"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Recurrence */}
                            <div className="p-4 bg-surface-containerLow rounded-xl">
                                <label className="flex items-center gap-3 cursor-pointer mb-4">
                                    <input
                                        type="checkbox"
                                        checked={formData.recurrence.enabled}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            recurrence: { ...formData.recurrence, enabled: e.target.checked }
                                        })}
                                        className="accent-primary w-5 h-5"
                                    />
                                    <span className="text-title-small font-bold">Wiederkehrendes Event</span>
                                </label>

                                {formData.recurrence.enabled && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Frequenz</label>
                                            <select
                                                value={formData.recurrence.frequency}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    recurrence: { ...formData.recurrence, frequency: e.target.value }
                                                })}
                                                className="md3-textfield-outlined"
                                            >
                                                <option value="weekly">Wöchentlich</option>
                                                <option value="biweekly">Alle 2 Wochen</option>
                                                <option value="monthly">Monatlich</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Wochentag</label>
                                            <select
                                                value={formData.recurrence.dayOfWeek}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    recurrence: { ...formData.recurrence, dayOfWeek: parseInt(e.target.value) }
                                                })}
                                                className="md3-textfield-outlined"
                                            >
                                                {weekDays.map((day, idx) => (
                                                    <option key={day} value={idx}>{day}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-outlineVariant">
                                <button type="submit" className="md3-btn-filled flex-1">
                                    {selectedTemplate ? 'Speichern' : 'Erstellen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="md3-btn-text flex-1"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Generate Event Modal */}
            {showGenerateModal && selectedTemplate && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-md w-full p-8 animate-scale-in">
                        <h2 className="text-title-large font-bold mb-2">Event erstellen</h2>
                        <p className="text-body-medium text-surface-onVariant mb-6">
                            Aus Vorlage: {selectedTemplate.name}
                        </p>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Datum *</label>
                                <input
                                    type="date"
                                    value={generateData.date}
                                    onChange={(e) => setGenerateData({ ...generateData, date: e.target.value })}
                                    className="md3-textfield-outlined"
                                />
                            </div>
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Titel (optional)</label>
                                <input
                                    type="text"
                                    value={generateData.customTitle}
                                    onChange={(e) => setGenerateData({ ...generateData, customTitle: e.target.value })}
                                    className="md3-textfield-outlined"
                                    placeholder={selectedTemplate.templateData?.title || 'Übernimmt Vorlage'}
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button onClick={generateEvent} className="md3-btn-filled flex-1">
                                <Play style={{ fontSize: 18 }} className="mr-1" />
                                Event erstellen
                            </button>
                            <button
                                onClick={() => { setShowGenerateModal(false); setGenerateData({ date: '', customTitle: '' }); }}
                                className="md3-btn-text flex-1"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventTemplates;
